import { useCallback, useEffect, useRef, useState } from "react";
import api from "./api";

/**
 * Feb 2026 · User-Directive „Glocken-Badge zeigt nur ungelesene
 * Wochenberichte" (5 Sep 2026).
 *
 * Analog zu `useUnreadQuotes` / `useUnreadMessages`. Pollt
 * `GET /weekly-updates/unread-count` alle 30 s und reagiert auf
 * `bumpUnreadUpdates()` (Fired sobald ein Bericht per Modal geoeffnet
 * → mark-read gerufen wurde), damit der Bell-Badge in `Layout.jsx`
 * sofort auf die neue Zahl faellt, ohne auf den naechsten Poll zu
 * warten.
 */
const UNREAD_UPDATES_EVENT = "weissblom:unread-updates-refresh";

export function bumpUnreadUpdates() {
  try {
    window.dispatchEvent(new CustomEvent(UNREAD_UPDATES_EVENT));
  } catch { /* older browsers — ignore */ }
}

export function useUnreadUpdates(enabled) {
  const [total, setTotal] = useState(0);
  const alive = useRef(true);

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await api.get("/weekly-updates/unread-count");
      if (!alive.current) return;
      setTotal(Number(data?.count || 0));
    } catch { /* silent — badge stays at last value */ }
  }, [enabled]);

  useEffect(() => {
    alive.current = true;
    if (!enabled) return () => { alive.current = false; };
    load();
    const iv = setInterval(load, 30000);
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    const onBump = () => load();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(UNREAD_UPDATES_EVENT, onBump);
    return () => {
      alive.current = false;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(UNREAD_UPDATES_EVENT, onBump);
    };
  }, [enabled, load]);

  return { total, refresh: load };
}
